import React, { useMemo, useEffect } from 'react';
import { StyleSheet, Pressable, Animated, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getShadowThemeStyles, isDarkTheme, PLUS_COLORS } from '@/constants/Colors';
import PropTypes from 'prop-types';
import useResponsiveness from '@/hooks/useResponsiveness';

export const getWarningLinkColor = (theme) =>
  isDarkTheme(theme) ? '#ffd470' : '#7a5a10';

const MAX_LEVEL = 5;
const BASE_WIDTH = 94;
const WIDTH_DECREASE = 5;
const ANIMATION_DURATION = 300;
const BANNER_HEIGHT = 26;

const createStyles = (colors, themeStyles, level = 1, isDetached, isXS, isCloseable, useOverlayCloseButton) => {
  const validLevel = Math.max(1, Math.min(level, MAX_LEVEL));
  const width = BASE_WIDTH - ((validLevel - 1) * WIDTH_DECREASE);
  
  const borderStyles = isXS ? {} : {
    borderTopWidth: isDetached ? 0 : 2,
    borderRightWidth: isDetached ? 0 : 2,
    borderLeftWidth: isDetached ? 0 : 2,
    borderTopColor: isDetached ? 'transparent' : themeStyles.borderColor,
    borderRightColor: isDetached ? 'transparent' : themeStyles.borderColor,
    borderLeftColor: isDetached ? 'transparent' : themeStyles.borderColor,
  };

  return StyleSheet.create({
    banner: {
      width: '100%',
      maxWidth: isDetached ? '100%' : `${width}%`,
      marginHorizontal: 'auto',
      paddingLeft: isDetached ? 10 : 15,
      paddingRight: isCloseable ? ((isXS || useOverlayCloseButton) ? 36 : 22) : (isDetached ? 20 : 15),
      paddingVertical: 5,
      ...borderStyles,
      ...(Platform.OS === 'web' ? {
        backdropFilter: 'blur(18px) saturate(1)',
        WebkitBackdropFilter: 'blur(18px) saturate(1)',
      } : {}),
      borderTopRightRadius: isDetached ? 0 : 10,
      borderTopLeftRadius: isDetached ? 0 : 10,
      flexDirection: 'row',
      alignItems: (isXS || useOverlayCloseButton) ? 'flex-start' : 'center',
      justifyContent: 'flex-start',
      gap: 4,
      marginBottom: 0,
      zIndex: validLevel,
      overflow: 'hidden',
      minHeight: (isXS || useOverlayCloseButton) ? 34 : BANNER_HEIGHT,
    },
    contentContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: 4,
    },
    bannerText: {
      textAlign: 'left',
    },
    closeButton: {
      ...((isXS || useOverlayCloseButton)
        ? {
          position: 'absolute',
          right: 6,
          top: 6,
          width: 24,
          height: 24,
          borderRadius: 12,
          alignItems: 'center',
          justifyContent: 'center',
        }
        : {
          paddingTop: 1,
          marginLeft: 8,
        }),
    },
    container: {
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
    }
  });
};

const getVariantStyles = (colors, variant, theme) => {
  if (variant === 'danger') {
    const opacity = '45';
    return {
      backgroundColor: colors.color_destructive + opacity,
      textColor: colors.text_color_primary,
    };
  } else if (variant === 'success') {
    return {
      backgroundColor: colors.color_success + '95',
      textColor: colors.text_color_primary,
    };
  } else if (variant === 'warning') {
    return {
      backgroundColor: isDarkTheme(theme) ? 'rgba(180, 130, 40, 0.35)' : 'rgba(180, 130, 40, 0.18)',
      textColor: isDarkTheme(theme) ? '#f0d080' : '#6b4f1a',
      linkColor: isDarkTheme(theme) ? '#ffd470' : '#7a5a10',
    };
  } else if (variant === 'upgrade') {
    return {
      backgroundColor: PLUS_COLORS.textColorPurple,
      ...(Platform.OS === 'web' && { backgroundImage: PLUS_COLORS.backgroundImage }),
      textColor: PLUS_COLORS.textColor,
    };
  }

  return {
    backgroundColor: colors.solid_color_secondary,
    textColor: colors.text_color_secondary,
  };
};

const ChatInputBanner = ({ 
  children,
  color = 'danger',
  level = 1,
  isCloseable = false,
  onClose,
  isDetached = false,
  useOverlayCloseButton = false,
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const themeStyles = getShadowThemeStyles(theme);
  const styles = useMemo(
    () => createStyles(colors, themeStyles, level, isDetached, isXS, isCloseable, useOverlayCloseButton),
    [colors, themeStyles, level, isDetached, isXS, isCloseable, useOverlayCloseButton]
  );
  const variantStyles = useMemo(() => getVariantStyles(colors, color, theme), [colors, color, theme]);

  // Animation value
  const translateY = useMemo(() => new Animated.Value(50), []); // Start from below
  const opacity = useMemo(() => new Animated.Value(1), []); // Start visible

  useEffect(() => {
    // Animate in on mount
    Animated.timing(translateY, {
      toValue: 0,
      duration: ANIMATION_DURATION,
      useNativeDriver: true,
    }).start();
  }, []);

  const handleClose = () => {
    // Animate out before setting opacity to 0
    Animated.timing(translateY, {
      toValue: 50,
      duration: ANIMATION_DURATION,
      useNativeDriver: true,
    }).start(() => {
      // Set opacity to 0 to hide the component
      opacity.setValue(0);
      if (onClose) onClose();
    });
  };

  return (
    <Animated.View 
      style={[
        styles.container,
        {
          transform: [{ translateY }],
          opacity: opacity
        }
      ]}
    >
      <View style={[
        styles.banner,
        variantStyles
      ]}>
        <View style={styles.contentContainer}>
          {React.Children.map(children, (child, index) => {
            // If child is a string or number, wrap in Text
            if (typeof child === 'string' || typeof child === 'number') {
              return (
                <Text key={index} size='s' style={[styles.bannerText, { color: variantStyles.textColor }]}>
                  {child}
                </Text>
              );
            }
            // Otherwise render the child as-is (e.g., Pressable, Button)
            return child;
          })}
        </View>
        {isCloseable && (
          <Pressable onPress={handleClose} style={styles.closeButton}>
            <Icon name="xmark" size={12} color={variantStyles.textColor} />
          </Pressable>
        )}
      </View>
    </Animated.View>
  );
};

ChatInputBanner.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.string,
  level: PropTypes.oneOf([1, 2, 3, 4, 5]),
  isCloseable: PropTypes.bool,
  onClose: PropTypes.func,
  useOverlayCloseButton: PropTypes.bool,
};

export default React.memo(ChatInputBanner);